import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";

// Lazy load all pages for better initial load performance
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const WaterDashboard = lazy(() => import("./pages/water/Dashboard"));
const WaterAddPeriod = lazy(() => import("./pages/water/AddPeriod"));
const WaterEditPeriod = lazy(() => import("./pages/water/EditPeriod"));
const WaterPeriodDetail = lazy(() => import("./pages/water/PeriodDetail"));
const GasDashboard = lazy(() => import("./pages/gas/Dashboard"));
const GasAddPeriod = lazy(() => import("./pages/gas/AddPeriod"));
const GasEditPeriod = lazy(() => import("./pages/gas/EditPeriod"));
const GasPeriodDetail = lazy(() => import("./pages/gas/PeriodDetail"));
const ElectricityDashboard = lazy(() => import("./pages/electricity/Dashboard"));
const ElectricityAddPeriod = lazy(() => import("./pages/electricity/AddPeriod"));
const ElectricityEditPeriod = lazy(() => import("./pages/electricity/EditPeriod"));
const ElectricityPeriodDetail = lazy(() => import("./pages/electricity/PeriodDetail"));
const Documentation = lazy(() => import("./pages/Documentation"));
const TechnicalDocumentation = lazy(() => import("./pages/TechnicalDocumentation"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const Install = lazy(() => import("./pages/Install"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/auth" element={<Auth />} />
              <Route path="/install" element={<Install />} />
              <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
              <Route path="/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
              <Route path="/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
              <Route path="/documentation" element={<ProtectedRoute><Documentation /></ProtectedRoute>} />
              <Route path="/technical-documentation" element={<ProtectedRoute><TechnicalDocumentation /></ProtectedRoute>} />
              
              {/* Water routes */}
              <Route path="/water" element={<ProtectedRoute><WaterDashboard /></ProtectedRoute>} />
              <Route path="/water/add-period" element={<ProtectedRoute><WaterAddPeriod /></ProtectedRoute>} />
              <Route path="/water/edit-period/:id" element={<ProtectedRoute><WaterEditPeriod /></ProtectedRoute>} />
              <Route path="/water/period/:id" element={<ProtectedRoute><WaterPeriodDetail /></ProtectedRoute>} />
              
              {/* Gas routes */}
              <Route path="/gas" element={<ProtectedRoute><GasDashboard /></ProtectedRoute>} />
              <Route path="/gas/add-period" element={<ProtectedRoute><GasAddPeriod /></ProtectedRoute>} />
              <Route path="/gas/edit-period/:id" element={<ProtectedRoute><GasEditPeriod /></ProtectedRoute>} />
              <Route path="/gas/period/:id" element={<ProtectedRoute><GasPeriodDetail /></ProtectedRoute>} />
              
              {/* Electricity routes */}
              <Route path="/electricity" element={<ProtectedRoute><ElectricityDashboard /></ProtectedRoute>} />
              <Route path="/electricity/add-period" element={<ProtectedRoute><ElectricityAddPeriod /></ProtectedRoute>} />
              <Route path="/electricity/edit-period/:id" element={<ProtectedRoute><ElectricityEditPeriod /></ProtectedRoute>} />
              <Route path="/electricity/period/:id" element={<ProtectedRoute><ElectricityPeriodDetail /></ProtectedRoute>} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
